import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconDeviceFloppy,
  IconMenu2,
  IconMessageCircleQuestion,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { type QuickReply } from './instagram.inbox';

/** Local, form-friendly persistent-menu row (union flattened for editing). */
interface MenuRow {
  type: 'postback' | 'web_url';
  title: string;
  payload: string;
  url: string;
}

function InstagramSettings() {
  const accounts = trpc.instagram.accounts.list.useQuery();
  const [accountId, setAccountId] = useState<number | null>(null);

  // Default to the first connected account once loaded.
  useEffect(() => {
    if (accountId == null && accounts.data && accounts.data.length > 0) {
      setAccountId(accounts.data[0]!.id);
    }
  }, [accounts.data, accountId]);

  const accountOptions = (accounts.data ?? []).map((a) => ({
    value: String(a.id),
    label: a.name ? `${a.name} (@${a.username})` : `@${a.username}`,
  }));

  return (
    <Stack p="md" gap="md">
      <Group justify="space-between">
        <Title order={3}>Instagram automation</Title>
        <Select
          w={280}
          placeholder="Select account"
          data={accountOptions}
          value={accountId != null ? String(accountId) : null}
          onChange={(v) => setAccountId(v ? Number(v) : null)}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
      </Group>

      {accounts.isPending ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : accountOptions.length === 0 ? (
        <Text c="dimmed">Connect an Instagram account first.</Text>
      ) : accountId == null ? null : (
        <Tabs defaultValue="iceBreakers" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="iceBreakers" leftSection={<IconMessageCircleQuestion size={16} />}>
              Ice breakers
            </Tabs.Tab>
            <Tabs.Tab value="menu" leftSection={<IconMenu2 size={16} />}>
              Persistent menu
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="iceBreakers" pt="md">
            <IceBreakerEditor accountId={accountId} />
          </Tabs.Panel>
          <Tabs.Panel value="menu" pt="md">
            <PersistentMenuEditor accountId={accountId} />
          </Tabs.Panel>
        </Tabs>
      )}
    </Stack>
  );
}

/** Up to 4 FAQ starter questions shown when a customer opens a new chat. */
function IceBreakerEditor({ accountId }: { accountId: number }) {
  const config = trpc.instagram.profile.get.useQuery({ instagramAccountId: accountId });
  const save = trpc.instagram.profile.setIceBreakers.useMutation({
    onSuccess: () => notifications.show({ message: 'Ice breakers saved', color: 'green' }),
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const [rows, setRows] = useState<QuickReply[]>([]);

  useEffect(() => {
    if (config.data) {
      setRows(config.data.iceBreakers.map((b) => ({ title: b.question, payload: b.payload })));
    }
  }, [config.data]);

  const valid = rows.every((r) => r.title.trim() && r.payload.trim());

  return (
    <Card withBorder maw={680}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Shown as tappable starters in a brand-new conversation. A tap sends the payload back as a
          postback so you can auto-reply. Max 4.
        </Text>
        {rows.map((r, i) => (
          <Group key={i} wrap="nowrap" gap="xs" align="flex-end">
            <TextInput
              flex={1}
              label={i === 0 ? 'Question' : undefined}
              placeholder="What are your hours?"
              maxLength={80}
              value={r.title}
              onChange={(e) =>
                setRows((rs) =>
                  rs.map((x, idx) => (idx === i ? { ...x, title: e.currentTarget.value } : x)),
                )
              }
            />
            <TextInput
              flex={1}
              label={i === 0 ? 'Payload' : undefined}
              placeholder="HOURS"
              value={r.payload}
              onChange={(e) =>
                setRows((rs) =>
                  rs.map((x, idx) => (idx === i ? { ...x, payload: e.currentTarget.value } : x)),
                )
              }
            />
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
              aria-label="Remove"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}
        <Group justify="space-between">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            disabled={rows.length >= 4}
            onClick={() => setRows((rs) => [...rs, { title: '', payload: '' }])}
          >
            Add ice breaker
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            color="pink"
            loading={save.isPending}
            disabled={!valid}
            onClick={() =>
              save.mutate({
                instagramAccountId: accountId,
                iceBreakers: rows.map((r) => ({
                  question: r.title.trim(),
                  payload: r.payload.trim(),
                })),
              })
            }
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/** Always-available menu (up to 5 actions) inside the DM thread. */
function PersistentMenuEditor({ accountId }: { accountId: number }) {
  const config = trpc.instagram.profile.get.useQuery({ instagramAccountId: accountId });
  const save = trpc.instagram.profile.setPersistentMenu.useMutation({
    onSuccess: () => notifications.show({ message: 'Persistent menu saved', color: 'green' }),
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const [rows, setRows] = useState<MenuRow[]>([]);

  useEffect(() => {
    if (config.data) {
      setRows(
        config.data.persistentMenu.map((m) => ({
          type: m.type,
          title: m.title,
          payload: m.type === 'postback' ? m.payload : '',
          url: m.type === 'web_url' ? m.url : '',
        })),
      );
    }
  }, [config.data]);

  const valid = rows.every(
    (r) => r.title.trim() && (r.type === 'web_url' ? r.url.trim() : r.payload.trim()),
  );

  return (
    <Card withBorder maw={680}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          A menu the customer can open any time in the conversation. Max 5 items.
        </Text>
        {rows.map((r, i) => (
          <Group key={i} wrap="nowrap" gap="xs" align="flex-end">
            <Select
              w={120}
              data={[
                { value: 'postback', label: 'Postback' },
                { value: 'web_url', label: 'Link' },
              ]}
              value={r.type}
              allowDeselect={false}
              onChange={(v) =>
                setRows((rs) =>
                  rs.map((x, idx) =>
                    idx === i ? { ...x, type: (v as 'postback' | 'web_url') ?? 'postback' } : x,
                  ),
                )
              }
            />
            <TextInput
              flex={1}
              placeholder="Talk to an agent"
              maxLength={30}
              value={r.title}
              onChange={(e) =>
                setRows((rs) =>
                  rs.map((x, idx) => (idx === i ? { ...x, title: e.currentTarget.value } : x)),
                )
              }
            />
            <TextInput
              flex={1}
              placeholder={r.type === 'web_url' ? 'https://…' : 'AGENT'}
              value={r.type === 'web_url' ? r.url : r.payload}
              onChange={(e) =>
                setRows((rs) =>
                  rs.map((x, idx) =>
                    idx === i
                      ? r.type === 'web_url'
                        ? { ...x, url: e.currentTarget.value }
                        : { ...x, payload: e.currentTarget.value }
                      : x,
                  ),
                )
              }
            />
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
              aria-label="Remove"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}
        <Group justify="space-between">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            disabled={rows.length >= 5}
            onClick={() =>
              setRows((rs) => [...rs, { type: 'postback', title: '', payload: '', url: '' }])
            }
          >
            Add menu item
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            color="pink"
            loading={save.isPending}
            disabled={!valid}
            onClick={() =>
              save.mutate({
                instagramAccountId: accountId,
                items: rows.map((r) =>
                  r.type === 'web_url'
                    ? { type: 'web_url', title: r.title.trim(), url: r.url.trim() }
                    : { type: 'postback', title: r.title.trim(), payload: r.payload.trim() },
                ),
              })
            }
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

export const Route = createFileRoute('/vendor/instagram/settings')({
  component: InstagramSettings,
});
